import { SingleLineConfiguration } from "../../../../../models/line-configurations.model";
import { OrderOQResponse } from "../../../../../models/order/api-models/order-oq-response.model";
import { PaymentProductResponse } from "../../../../../models/order/api-models/order-payment-response.model";
import { WirelessPDPlusLine, WirelessPDPlusResponse } from "../../../../../models/order/api-models/order-pd-plus-response.model";
import { Choice } from "../../../../../models/order/api-models/order-pd-response.model";
import { ProductType } from "../../../../../models/order/order.model";
import { CartItem, CartItemType, CartSectionType } from "../../../../../models/order/workflow/order-cart.model";
import { LineConfigType, WirelessCartCustomizationType, WirelessCartSelectedCustomization } from "../../../../../models/order/workflow/wireless/wireless-cart.model";
import { WirelessFlow } from "../../../../../models/order/workflow/wireless/wireless-flow.model";
import { PDPlus_Choice_SubGroup_FAN_Discount_Name } from '../../../../../../common/constants/app-constants';
export enum WirelessCartRuleType {
    Line = 'Line',
    Customization = 'Customization',
    OrderQualification = 'OrderQualification'
}

export interface WirelessCartRule {
    name: string;
    priority: number;
    condition: (context: WirelessCartRuleContext) => boolean;
    execute: (context: WirelessCartRuleContext) => void;
    nestedRules?: WirelessCartRule[];
    allowNested?: boolean; // Flag to allow nested execution
    type: WirelessCartRuleType;
}

export interface WirelessRuleEngineResult {
    cartItems: CartItem;
    selectedCustomizations: WirelessCartSelectedCustomization;
}

export interface WirelessCartRuleContext {
    selectedCustomizations: WirelessCartSelectedCustomization;
    allSelectedCustomizations: WirelessCartSelectedCustomization[];
    lineData?: SingleLineConfiguration;
    cartItems: CartItem[];
    lineNumber?: number;
    wirelessPDPlusData: { [lineNumber: string]: WirelessPDPlusResponse };
    activeLineNumber?: number; // Optional line number for line-specific rules
    oqData?: OrderOQResponse;
    viPaymentData?: PaymentProductResponse;
    cardType?: string | null;
    wirelessStateData?: WirelessFlow
}

export abstract class WirelessCartRuleBase {
    protected createCartItem(offer: WirelessCartSelectedCustomization): CartItem {
        const cartItem: CartItem = {
            disclosure: offer.Choice?.CartDisclosure || '',
            amount: offer.Choice?.Fee?.Fee || 0,
            name: offer.Choice?.Name || '',
            choiceCode: offer.Choice?.ChoiceCode || '',
            seq: offer.Choice?.CartSeq || 0,
            type: offer.lineType === LineConfigType.Promotion ? CartItemType.Promotion : CartItemType.Fee,
            choice: offer.Choice,
            customization: offer.Customization,
            cartSectionType: CartSectionType.Monthly, // default to monthly, can be overridden in context
            metadata: offer,
            productType: ProductType.Wireless
        };
        return cartItem;
    }

    protected getLineCustomizations(context: WirelessCartRuleContext): WirelessCartSelectedCustomization[] {
        return context.allSelectedCustomizations?.filter(cust => cust.lineNumber === context.lineNumber) || [];
    }

    protected findLineCustomizationBySubGroupName(context: WirelessCartRuleContext, subGroupName: string): WirelessCartSelectedCustomization[] | null {
        const allCustomizations = this.getLineCustomizations(context);
        const found = allCustomizations.filter(cust => cust.Customization.SubGroupName === subGroupName);
        return found || null;
    }

    protected findLineCustomizationByChoiceCode(context: WirelessCartRuleContext, choiceCode: string): WirelessCartSelectedCustomization | null {
        const allCustomizations = this.getLineCustomizations(context);
        const found = allCustomizations.find(cust => cust.Choice.ChoiceCode === choiceCode);
        return found || null;
    }

    protected getPDPlusLineData(context: WirelessCartRuleContext, lineNumber: number): WirelessPDPlusResponse | null {
        if (lineNumber && context.wirelessPDPlusData) {
            return context.wirelessPDPlusData[`PDPLUS_${lineNumber}`] || null;
        }
        return null;
    }

    protected getPDPlusWirelessLine(context: WirelessCartRuleContext, lineNumber: number): WirelessPDPlusLine {
        const pdPlusLineData = this.getPDPlusLineData(context, lineNumber);
        if (pdPlusLineData) {
            return pdPlusLineData.Components.Component.WirelessLines.WirelessLine.find((line: any) => +line.LineNumber === lineNumber);
        }
        return null;
    }

    protected getLatestPDPlusWirelessLine(context: WirelessCartRuleContext, lineNumber: number): WirelessPDPlusLine | null {
        const latestLineData = this.getPDPlusLineData(context, context.activeLineNumber);
        const latestWirelessLine = latestLineData?.Components?.Component?.WirelessLines?.WirelessLine?.find(line => +line.LineNumber === lineNumber);
        if (latestWirelessLine) {
            return latestWirelessLine;
        }

        const lineData = this.getPDPlusLineData(context, lineNumber);
        return lineData?.Components?.Component?.WirelessLines?.WirelessLine?.find(line => +line.LineNumber === lineNumber) || null;
    }

    protected getPDPlusActiveLineData(context: WirelessCartRuleContext): WirelessPDPlusResponse | null {
        if (context.activeLineNumber !== undefined && context.wirelessPDPlusData) {
            return this.getPDPlusLineData(context, context.activeLineNumber);
        }
        return null;
    }

    protected isMatchingLineNumber(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations.lineNumber === context.lineNumber;
    }

    protected isCartVisible(offer: WirelessCartSelectedCustomization): boolean {
        if (offer.Choice && offer.Customization && (offer.Choice?.CartVisible?.toLowerCase() === 'true' || offer.Customization?.CartVisible?.toLowerCase() === 'true')) {
            return true;
        }
        return false;
    }
}

export class OQResponseTodaysChargesCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'oqResponseTodaysCharges';
    priority = 3500;
    type: WirelessCartRuleType = WirelessCartRuleType.OrderQualification;
    condition(context: WirelessCartRuleContext): boolean {
        const oqComponent = context.oqData?.Components?.Component.find(comp => comp.ProductType === 'WIRELESS');
        if (oqComponent) {
            return true;
        }
        return false;
    }
    execute(context: WirelessCartRuleContext): void {
        const oqComponent = context.oqData?.Components?.Component.find(comp => comp.ProductType === 'WIRELESS');
        const orderTaxAmount = oqComponent?.OrderTaxAmount ? +oqComponent.OrderTaxAmount : 0;
        // const totalDownPaymentOnHand = oqComponent?.WirelessLines?.TotalDownPaymentOnHand ? +oqComponent.WirelessLines?.TotalDownPaymentOnHand : 0;
        const totalDepositAmount = oqComponent?.WirelessLines?.TotalDepositAmount ? +oqComponent.WirelessLines?.TotalDepositAmount : 0;
        // const orderTotalAmountOnHand = oqComponent?.OrderTotalAmountOnHand ? +oqComponent.OrderTotalAmountOnHand : 0;

        if (orderTaxAmount > 0) {
            const cartItem: CartItem = {
                name: `AT&T Wireless Sales Tax`,
                amount: orderTaxAmount,
                choiceCode: 'wirelessOQResponseOrderTaxAmount',
                seq: 102,
                type: CartItemType.Fee,
                disclosure: '',
                metadata: {} as WirelessCartSelectedCustomization,
                cartSectionType: CartSectionType.TodaysCharges,
                productType: ProductType.Wireless
            }
            context.cartItems.push(cartItem);
        }

        // if (totalDownPaymentOnHand > 0) {
        //     const cartItem: CartItem = {
        //         name: `Wireless Downpayment On Hand`,
        //         amount: totalDownPaymentOnHand,
        //         choiceCode: 'wirelessOQResponseTotalDownPaymentOnHand',
        //         seq: 103,
        //         type: CartItemType.Fee,
        //         disclosure: '',
        //         metadata: {} as WirelessCartSelectedCustomization,
        //         cartSectionType: CartSectionType.TodaysCharges
        //     }
        //     context.cartItems.push(cartItem);
        // }

        if (totalDepositAmount > 0) {
            const cartItem: CartItem = {
                name: `Wireless Deposit Amount`,
                amount: totalDepositAmount,
                choiceCode: 'wirelessOQResponseTotalDepositAmount',
                seq: 104,
                type: CartItemType.Fee,
                disclosure: '',
                metadata: {} as WirelessCartSelectedCustomization,
                cartSectionType: CartSectionType.TodaysCharges,
                productType: ProductType.Wireless
            }
            context.cartItems.push(cartItem);
        }

        // if (orderTotalAmountOnHand > 0) {
        //     const cartItem: CartItem = {
        //         name: `AT&T Wireless Total Due Today`,
        //         amount: orderTotalAmountOnHand,
        //         choiceCode: 'wirelessOQResponseOrderTotalAmountOnHand',
        //         seq: 105,
        //         type: CartItemType.Fee,
        //         disclosure: '',
        //         metadata: {} as WirelessCartSelectedCustomization,
        //         cartSectionType: CartSectionType.TodaysCharges
        //     }
        //     context.cartItems.push(cartItem);
        // }
    }
}

export class WirelessSelectedDeviceCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'newWirelessSelectedDevice';
    priority = 9000;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations.lineType === LineConfigType.Device && this.isMatchingLineNumber(context);
    }
    execute(context: WirelessCartRuleContext): void {
        const lineData = context.lineData;
        const deviceType = lineData?.device?.deviceType;
        let deviceChoiceCode = '';
        if (deviceType === 'BYOD') {
            deviceChoiceCode = deviceType;
        }
        else {
            deviceChoiceCode = lineData?.device?.selectedDevice?.ChoiceCode;
        }

        const selectedDeviceCustomization = this.findLineCustomizationByChoiceCode(context, deviceChoiceCode);
        if (selectedDeviceCustomization) {
            for (const cust of selectedDeviceCustomization.Customization.Choices.Choice) {
                const choice = this.findLineCustomizationByChoiceCode(context, cust.ChoiceCode);
                if (cust.ChoiceCode === deviceChoiceCode) {
                    const cartItem = this.createCartItem(choice);
                    cartItem.cartSectionType = CartSectionType.Device;
                    context.cartItems.push(cartItem);
                    break;
                }
            }
        }

        //We also need to get device
        const wirelessIncludedOffers = this.findLineCustomizationBySubGroupName(context, 'WirelessIncludedOffers');
        const includedOffers = this.findLineCustomizationBySubGroupName(context, 'IncludedOffers');
        //const wirelessPromotions = this.findLineCustomizationBySubGroupName(context, 'WirelessPromotion');
        // wirelessPromotions.push(...(includedOffers || []));
        // wirelessPromotions.push(...(wirelessIncludedOffers || []));
        const wirelessPromotions = [...includedOffers, ...wirelessIncludedOffers];

        if (wirelessPromotions) {
            for (const cust of wirelessPromotions) {
                if (cust.Customization.Choices.Choice.length === 1 && cust.Customization.Required?.toLowerCase() === 'true') {
                    const choice = this.findLineCustomizationByChoiceCode(context, cust.Customization.Choices.Choice[0].ChoiceCode);
                    if (choice && this.isCartVisible(choice)) {
                        const cartItem = this.createCartItem(choice);
                        cartItem.type = CartItemType.Promotion;
                        if (choice.Choice.CartCategory === 'Wireless' || choice.Choice.Fee.FeeTerm === 'Monthly') {
                            cartItem.cartSectionType = CartSectionType.Monthly;
                        } else {
                            cartItem.cartSectionType = CartSectionType.SavingsAndPromotions;
                        }

                        if (!context.cartItems.find(item => item.choiceCode === cartItem.choiceCode)) {
                            context.cartItems.push(cartItem);
                        }
                    }
                }

            }
        }

    }
}

export class WirelessSelectedDeviceDefaultChoicesCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'newWirelessSelectedDeviceDefaultChoices';
    priority = 9500;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations.lineType === LineConfigType.Device && this.isMatchingLineNumber(context);
    }
    execute(context: WirelessCartRuleContext): void {
        const lineData = context.lineData;
        const deviceType = lineData?.device?.deviceType;
        let deviceChoiceCode = '';
        if (deviceType === 'BYOD') {
            deviceChoiceCode = deviceType;
        }
        else {
            deviceChoiceCode = lineData?.device?.selectedDevice?.ChoiceCode;
        }

        const selectedDeviceCustomization = this.findLineCustomizationByChoiceCode(context, deviceChoiceCode);
        if (selectedDeviceCustomization) {
            for (const cust of selectedDeviceCustomization.Customization.Choices.Choice) {
                const choice = this.findLineCustomizationByChoiceCode(context, cust.ChoiceCode);
                if (choice && this.isCartVisible(choice)) {
                    if (cust.ChoiceCode === deviceChoiceCode) continue; //Skip selected device as it will be added by other rule
                    const isABP = choice.Choice.ChoiceSubGroupName === 'WirelessABPDiscount';
                    if (isABP) {
                        //Skip ABP from default choices as it will be added by promotion rule
                        continue;
                    }
                    const cartItem = this.createCartItem(choice);
                    if (choice.Choice.ChoiceSubGroupName === PDPlus_Choice_SubGroup_FAN_Discount_Name) {
                        const wirelessLine = this.getLatestPDPlusWirelessLine(context, context.lineNumber);
                        if (!isNaN(Number(wirelessLine?.LineFanDiscount))) {
                            cartItem.amount = -Number(wirelessLine.LineFanDiscount);
                        }
                    }
                    if (choice.Choice.CartCategory?.toLocaleLowerCase() === 'firstbill') {
                        cartItem.cartSectionType = CartSectionType.FirstBill;
                    }
                    else if (choice.Choice.CartCategory?.toLocaleLowerCase() === 'todayscharges') {
                        cartItem.cartSectionType = CartSectionType.TodaysCharges;
                    }
                    else if (choice.Choice.CartCategory?.toLocaleLowerCase() === 'wireless') {
                        cartItem.cartSectionType = CartSectionType.Monthly;
                        if (cartItem.amount < 0 && cartItem.type === CartItemType.Fee) {
                            cartItem.type = CartItemType.Promotion;
                        }
                    }
                    context.cartItems.push(cartItem);
                }
            }
        }
    }
}

export class WirelessSelectedPlanCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'newWirelessSelectedPlan';
    priority = 10000;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations.lineType === LineConfigType.Plan && this.isMatchingLineNumber(context) && this.isCartVisible(context.selectedCustomizations) && this.isNewLineOrExistingLineTouched(context);
    }
    execute(context: WirelessCartRuleContext): void {
        const cartItem = this.createCartItem(context.selectedCustomizations);
        const wirelessPdPlusLinedata = context.wirelessPDPlusData ? context.wirelessPDPlusData[`PDPLUS_${context.activeLineNumber}`] : null;
        if (wirelessPdPlusLinedata) {
            const wirelessline = wirelessPdPlusLinedata.Components?.Component?.WirelessLines?.WirelessLine?.find(line => +line.LineNumber === context.lineNumber);
            if (wirelessline && +wirelessline.LineDataPlanCost > 0) {
                cartItem.amount = +wirelessline.LineDataPlanCost;
            }
        }
        context.cartItems.push(cartItem);
    }

    isNewLineOrExistingLineTouched(context: WirelessCartRuleContext): boolean {
        if (context.lineData?.device.transactionType !== 'existing' && context.lineData?.device.transactionType !== 'upgrade') {
            return true;
        }
        return context.lineData?.device?.upgradeProcessInitiated || context.lineData?.upgradeDevice?.preUpgradeProcessCompleted;
    }
}

export class WirelessSelectedInstallmentPlanCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'newWirelessSelectedInstallmentPlan';
    priority = 11000;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return this.isContractPlan(context.selectedCustomizations.Choice) && context.selectedCustomizations.lineType === LineConfigType.Installment && this.isMatchingLineNumber(context) && this.isCartVisible(context.selectedCustomizations);
    }
    execute(context: WirelessCartRuleContext): void {
        const cartItem = this.createCartItem(context.selectedCustomizations);
        cartItem.amount = this.calculateMonthlyCost(context);
        context.cartItems.push(cartItem);
    }

    calculateMonthlyCost(context: WirelessCartRuleContext): number {
        const choice = context.selectedCustomizations.Choice;
        const wirelessLine = this.getLatestPDPlusWirelessLine(context, context.lineNumber);
        const requiredDownPayment = wirelessLine ? +wirelessLine.CrsmDownPayment : 0;
        if (context && +choice.ContractTerm > 0) {
            const optionalDownPayment = context.lineData?.installmentPlan?.optionalDownPaymentAmount || 0;
            const perMonth = (choice?.DeviceMsrpAmount - (optionalDownPayment + requiredDownPayment)) / +choice.ContractTerm;
            return perMonth.ceil(perMonth);
        }
        return 0;
    }

    isContractPlan(choice: Choice): boolean {
        return +choice?.ContractTerm > 0;
    }
}

export class InstallmentNextUpAnytimeCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'installmentNextUpAnytime';
    priority = 11001;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations.Choice?.ChoiceCode === 'WRLS-CPLAN-IP-SP-NU-10-36' && context.selectedCustomizations.lineType === LineConfigType.Installment && this.isMatchingLineNumber(context) && this.isCartVisible(context.selectedCustomizations);
    }
    execute(context: WirelessCartRuleContext): void {
        const cartItem = this.createCartItem(context.selectedCustomizations);
        cartItem.name = 'NextUp Anytime Charge';
        cartItem.disclosure = ''; //We dont need disclosure for this
        context.cartItems.push(cartItem);
    }
}

export class NoContractDeviceCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'noContractDevice';
    priority = 11002;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return +context.selectedCustomizations.Choice?.ContractTerm == 0 && context.selectedCustomizations.lineType === LineConfigType.Installment && this.isMatchingLineNumber(context) && this.isCartVisible(context.selectedCustomizations);
    }
    execute(context: WirelessCartRuleContext): void {
        const cartItem = this.createCartItem(context.selectedCustomizations);
        cartItem.cartSectionType = CartSectionType.TodaysCharges;
        const deviceInfo = context.allSelectedCustomizations.find(cust => cust.lineNumber === context.lineNumber && cust.lineType === LineConfigType.Device);
        if (deviceInfo) {
            cartItem.name = `${deviceInfo.Choice?.Name}(${deviceInfo.lineNumber})`;
            cartItem.amount = +context.selectedCustomizations.Choice.DeviceMsrpAmount || 0;
        }
        context.cartItems.push(cartItem);
    }
}

export class WirelessLineOptionalDownpaymentCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'wirelessLineOptionalDownpayment';
    priority = 11003;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {

        const wirelessLine = this.getLatestPDPlusWirelessLine(context, context.lineNumber);
        const optionalDownPayment = context.lineData?.installmentPlan?.optionalDownPaymentAmount;
        const deviceCost = context.lineData?.device?.selectedDevice?.Fee?.Fee || 0;
        const requiredDownPayment = wirelessLine ? +wirelessLine.CrsmDownPayment : 0;
        const finalAmount = deviceCost - (requiredDownPayment + optionalDownPayment);
        if (optionalDownPayment && optionalDownPayment > 0 && context.selectedCustomizations.lineType === LineConfigType.Installment && this.isMatchingLineNumber(context) && finalAmount > 50) {
            return true;
        }

        return false;
    }
    execute(context: WirelessCartRuleContext): void {
        // const deviceType = context.lineData?.device?.deviceType;
        const optionalDownPayment = context.lineData?.installmentPlan?.optionalDownPaymentAmount;
        const cartItem: CartItem = {
            name: `Optional Downpayment Line ${context.lineNumber}`,
            amount: optionalDownPayment,
            choiceCode: 'wirelessLineOptionalDownpayment',
            seq: 101,
            type: CartItemType.Fee,
            disclosure: '',
            metadata: {} as WirelessCartSelectedCustomization,
            cartSectionType: CartSectionType.TodaysCharges,
            productType: ProductType.Wireless
            // isRequired: !(deviceType === 'ONHAND')
        }
        context.cartItems.push(cartItem);
    }
}

export class WirelessLineRequiredDownpaymentCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'wirelessLineRequiredDownpayment';
    priority = 11004;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        const wirelessLine = this.getLatestPDPlusWirelessLine(context, context.lineNumber);
        if (wirelessLine && +wirelessLine.CrsmDownPayment > 0 && context.selectedCustomizations.lineType === LineConfigType.Device && this.isMatchingLineNumber(context)) {
            return true;
        }

        return false;
    }
    execute(context: WirelessCartRuleContext): void {
        const wirelessLine = this.getLatestPDPlusWirelessLine(context, context.lineNumber);
        const cartItem: CartItem = {
            name: `Wireless Downpayment Line ${context.lineNumber}`,
            amount: +wirelessLine.CrsmDownPayment,
            choiceCode: 'wirelessLineRequiredDownpayment',
            seq: 100,
            type: CartItemType.Fee,
            disclosure: '',
            metadata: {} as WirelessCartSelectedCustomization,
            cartSectionType: CartSectionType.TodaysCharges,
            productType: ProductType.Wireless
        }
        context.cartItems.push(cartItem);
    }
}

export class WirelessProtectionPlanCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'wirelessProtectionPlan';
    priority = 12000;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations.lineType === LineConfigType.Protection && this.isMatchingLineNumber(context) && this.isCartVisible(context.selectedCustomizations);
    }
    execute(context: WirelessCartRuleContext): void {
        const cartItem = this.createCartItem(context.selectedCustomizations);
        context.cartItems.push(cartItem);
    }
}

export class WirelessPromotionCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'wirelessPromotion';
    priority = 13000;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations.lineType === LineConfigType.Promotion && this.isMatchingLineNumber(context) && this.isCartVisible(context.selectedCustomizations);
    }
    execute(context: WirelessCartRuleContext): void {
        if (this.isABPOffer(context)) return; //Skip ABP here, it will be handled in separate rule
        const cartItem = this.createCartItem(context.selectedCustomizations);
        if (context.selectedCustomizations.Choice.CartCategory === 'Wireless' || context.selectedCustomizations.Choice.Fee.FeeTerm === 'Monthly') {
            cartItem.cartSectionType = CartSectionType.Monthly;
        } else {
            cartItem.cartSectionType = CartSectionType.TodaysCharges;
        }
        context.cartItems.push(cartItem);
    }

    isABPOffer(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations.Customization.SubGroupName === 'WirelessABP';
    }
}

export class WirelessABPDiscountCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'wirelessABPDiscount';
    priority = 14000;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        const isABP = context.selectedCustomizations.Customization.SubGroupName === 'WirelessABP';
        return isABP && context.selectedCustomizations.lineType === LineConfigType.Promotion && this.isMatchingLineNumber(context) && this.isCartVisible(context.selectedCustomizations) && context.lineData?.device?.selectedDevice?.DeviceType !== 'Wearable';
    }
    execute(context: WirelessCartRuleContext): void {
        const cartItem = this.createCartItem(context.selectedCustomizations);
        cartItem.type = CartItemType.Promotion;
        cartItem.cartSectionType = CartSectionType.Monthly;
        const autoPayAmount = context.viPaymentData?.AutopayAmount >= 0 ? context.viPaymentData.AutopayAmount : -1;
        if (autoPayAmount >= 0) {
            cartItem.amount = autoPayAmount > 0 ? -autoPayAmount : autoPayAmount;
        } else if (context.cardType?.toLowerCase() === 'debit') {
            cartItem.amount = cartItem.amount > 0 ? cartItem.amount - 5 : cartItem.amount + 5;
        } else if (context.cardType?.toLowerCase() === 'credit') {
            cartItem.amount = 0;
        }
        context.cartItems.push(cartItem);
    }
}

export class WirelessExistingABPDiscountCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'wirelessExistingABPDiscount';
    priority = 14001;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        const isABP = context.selectedCustomizations.Customization.CustomizationCode == 'WirelessABPExisting';
        return isABP && context.selectedCustomizations.lineType === LineConfigType.Promotion && this.isMatchingLineNumber(context);
    }
    execute(context: WirelessCartRuleContext): void {
        const cartItem: CartItem = {
            disclosure: '',
            amount: -10,
            name: 'Autopay/Paperless Discount',
            choiceCode: 'ATT-INTERNET-ABP-BOTH-YES',
            seq: 1,
            type: CartItemType.Promotion,
            choice: { Name: 'Autopay/Paperless Discount', ChoiceCode: 'ATT-INTERNET-ABP-BOTH-YES', CartSeq: 1 },
            customization: context.selectedCustomizations.Customization,
            cartSectionType: CartSectionType.Monthly, // default to monthly, can be overridden in context
            metadata: context.selectedCustomizations,
            productType: ProductType.Wireless
        };
        cartItem.type = CartItemType.Promotion;
        cartItem.cartSectionType = CartSectionType.Monthly;

        if (context.viPaymentData?.AutopayAmount && context.viPaymentData?.AutopayAmount > 0) {
            cartItem.amount = -context.viPaymentData.AutopayAmount;
        } else if (context.viPaymentData?.AutopayAmount && Math.trunc(context.viPaymentData?.AutopayAmount) === 0) {
            cartItem.amount = context.viPaymentData.AutopayAmount;
        }
        context.cartItems.push(cartItem);
    }
}

export class WirelessDeliveryServiceOptionCartRule extends WirelessCartRuleBase implements WirelessCartRule {
    name = 'wirelessDeliveryServiceOption';
    priority = 14001;
    type: WirelessCartRuleType = WirelessCartRuleType.Customization;
    condition(context: WirelessCartRuleContext): boolean {
        return context.selectedCustomizations?.Choice && context.wirelessStateData?.serviceLevelSelection?.serviceLevel !== undefined && context.selectedCustomizations?.Customization?.SubGroupName?.toLocaleLowerCase() === 'DeliveryServiceOptions'.toLocaleLowerCase() && context.selectedCustomizations.type === WirelessCartCustomizationType.Global;
    }
    execute(context: WirelessCartRuleContext): void {

        const selectedChoice = context.selectedCustomizations?.Choice;

        const cartItem: CartItem = {
            disclosure: '',
            amount: selectedChoice?.Fee?.Fee || 0,
            name: selectedChoice.Name,
            choiceCode: selectedChoice?.ChoiceCode || '',
            seq: 1,
            type: CartItemType.Fee,
            choice: selectedChoice,
            customization: context.selectedCustomizations.Customization,
            cartSectionType: CartSectionType.TodaysCharges,
            metadata: context.selectedCustomizations,
            productType: ProductType.Wireless
        };

        const oqComponent = context.oqData?.Components?.Component.find(comp => comp.ProductType?.toUpperCase() === 'WIRELESS');

        if (oqComponent?.OrderDeliveryFee && !isNaN(+oqComponent?.OrderDeliveryFee)) {
            cartItem.amount = +oqComponent?.OrderDeliveryFee;
        }

        context.cartItems.push(cartItem);
    }
}